import { ContextUserType, Separator, Button, Avatar, IUser } from '@ui/components';
import dayjs from 'dayjs';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/navigation';
import { Edit } from 'lucide-react';
import { useState } from 'react';

interface CommunitySidebarProps {
  userType: ContextUserType;
  communitySet: any;
  masterCommunitySet: any;
  isCommunitySuccess?: boolean;
  isLoadingCommunity?: boolean;
  userId?: string;
  onViewReports: () => void;
}

export const CommunitySidebar = ({
  userType,
  communitySet,
  masterCommunitySet,
  userId,
  onViewReports,
}: CommunitySidebarProps) => {
  const router = useRouter();
  const community = communitySet?.conversation;
  const restrictions = masterCommunitySet?.restrictions_list;
  const moderatorsProfiles = (restrictions?.moderators_profiles || []) as IUser[];
  const displayedModerators = moderatorsProfiles.slice(0, 6);
  const moderatorsIds = restrictions?.moderators_ids || [];
  const creatorId = masterCommunitySet?.creator_id || community?.creator_id;

  // Check if current user is moderator (creator or in moderators list)
  const isModerator =
    userType === 'creator' || (userId && (userId === creatorId || moderatorsIds.includes(userId)));

  const onViewAllModerators = () => {
    if (userType === 'creator') {
      router.push('/dashboard/community/moderators');
    } else {
      router.push(
        `/creator/community/moderators?creatorId=${
          masterCommunitySet.creator_id || community?.creator_id
        }`,
      );
    }
  };

  const onEditCommunity = () =>
    router.push(`/dashboard/community/edit?conversation_id=${community.id}`);
  // const onEditCommunity = () => router.push(`/dashboard/community/edit`);

  if (!community) return null;

  return (
    <div className="w-full lg:w-[300px] space-y-4">
      <div className="lg:rounded lg:border border-gray-200 lg:bg-white lg:p-4 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <h2 className="text-md font-semibold">{community.name}</h2>
            {userType === 'creator' && (
              <Button
                type="icon"
                size="smallIcon"
                onClick={onEditCommunity}
                className="h-8 w-8 lg:w-8 lg:h-8">
                <Edit className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-sm">{community.description}</p>
          </div>
        </div>

        <Link
          href={
            userType === 'creator' ? '/dashboard/community-guidelines' : '/community-guidelines'
          }
          className={twMerge('text-xs underline hover:text-primary')}>
          Community Guidelines
        </Link>

        <Separator />

        {/* {community?.conversation_type == 'community' && ( */}
        <div>
          <h2 className="text-md font-semibold mb-2">Moderators</h2>
          {moderatorsProfiles?.length > 0 ? (
            <div className="space-y-2">
              {displayedModerators.map((moderator) => (
                <div
                  key={moderator.id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  onClick={() =>
                    router.push(
                      `/fan-profile/?fanId=${moderator.id}&creatorId=${community.creator_id}`,
                    )
                  }>
                  <Avatar
                    size="s"
                    src={moderator.avatarURL}
                    displayName={moderator?.userName}
                    type="fan"
                  />
                  <span className="text-sm">{moderator.userName}</span>
                </div>
              ))}

              <Button
                type="secondary"
                size="small"
                className="w-full mt-2"
                onClick={onViewAllModerators}>
                View all
              </Button>
            </div>
          ) : (
            userType === 'creator' && (
              <div className="py-4 space-y-3">
                <p className="text-sm text-gray-500">This community currently has no moderators.</p>
                <Button
                  type="primary"
                  size="small"
                  onClick={() =>
                    router.push(
                      `/dashboard/community/my-fans/?conversation_id=${masterCommunitySet?.id}`,
                    )
                  }>
                  Add Moderators
                </Button>
              </div>
            )
          )}
        </div>
        {/* )} */}

        {/* Reports section - only visible to moderators */}
        {isModerator && (
          <>
            <Separator />
            <div>
              <Button type="secondary" size="small" className="w-full" onClick={onViewReports}>
                View Reports
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
